import styled from '@emotion/styled'
import { mergeStyles, StylesConfig } from 'react-select'
import { Primary, roboto, mixins, hexRgba } from '@sphinx/components-styles'
import { selectCustomStyles } from '../select/select.styles'
import { identifiers } from './multi-select.constants'

const controlMinHeightBase = 41
const controlChildHeightBase = 30

export const HiddenOptionsCounter = styled.div`
  ${mixins.caption};
  position: absolute;
  right: 5px;
  bottom: 2px;
  line-height: 14px;
  color: ${Primary.navy[100]};
`

export const getControlMinHeight = ({
  id,
  displayRows,
  isFocused,
  optionGapInPx,
  optionsContainerWidth,
}: {
  id: string
  displayRows: number
  isFocused: boolean
  optionGapInPx: number
  optionsContainerWidth: number
}) => {
  if (displayRows == 1 && !isFocused && id) {
    const hiddenOptionWidth = 18
    const selectedOptions = document.querySelectorAll(
      `[data-id=${id}__${identifiers.option}]`
    )

    let index = 0
    let prevTotalWidth = 0
    let totalWidth = 0

    while (
      totalWidth < optionsContainerWidth &&
      index < selectedOptions.length
    ) {
      const option = selectedOptions.item(index)
      const elementWidth =
        (option as HTMLDivElement).offsetWidth + optionGapInPx
      prevTotalWidth = totalWidth
      totalWidth += elementWidth
      index += 1
    }
    /**
     * prevTotalWidth will have the width of the first row
     * by subtracting it from optionsContainerWidth we know
     * if we have enough space to show option indicator
     */
    if (optionsContainerWidth - prevTotalWidth < hiddenOptionWidth) {
      return '54px'
    }
  }
  return `${displayRows * controlMinHeightBase}px`
}

export const getSelectCustomStyles = ({
  displayRows = 2,
  id,
  optionGapInPx,
  readOnly,
  optionsContainerWidth,
}: {
  id: string
  optionGapInPx: number
  optionsContainerWidth: number
  readOnly: boolean
  displayRows: number
}): Partial<StylesConfig> => {
  const defaultStyles = mergeStyles(selectCustomStyles, {
    control: (provided, { isFocused }) => ({
      ...provided,
      display: 'flex',
      alignItems: 'flex-start',
      height: 'fit-content',
      minHeight: getControlMinHeight({
        id,
        displayRows,
        isFocused,
        optionGapInPx,
        optionsContainerWidth,
      }),
      // options container
      '& > div:first-of-type': {
        padding: 0,
        margin: '4px',
        position: 'static',
        alignItems: 'flex-start',
        height: isFocused
          ? '100%'
          : `${displayRows * controlChildHeightBase}px`,
      },
      // hidden options counter
      '& > div#hidden-opts-number': {
        cursor: 'pointer',
        display: isFocused ? 'none' : 'initial',
      },
    }),
    indicatorsContainer: () => ({
      display: 'none',
    }),
    dropdownIndicator: () => ({
      display: 'none',
    }),
    placeholder: (provided) => ({
      ...provided,
      top: '10px',
      left: '5px',
      transform: 'translateY(0)',
    }),
    menu: (provided, { selectProps: { isLoading } }) => ({
      ...provided,
      height: isLoading && 0,
    }),
    option: (provided, { isSelected, isFocused, isDisabled }) => ({
      ...provided,
    }),
    noOptionsMessage: (provided) => ({
      ...provided,
      display: 'block',
      fontFamily: roboto,
      fontSize: '12px',
      lineHeight: '16px',
    }),
    multiValue: (provided) => ({
      ...provided,
      height: '28px',
      padding: '7px 10px 7px 8px',
      margin: `0 ${optionGapInPx}px ${optionGapInPx}px 0`,

      '& div:first-of-type': {
        padding: 0,
        fontFamily: roboto,
        fontSize: '12px',
        lineHeight: '14px',
        color: Primary.navy[100],
        paddingRight: '7px',
      },

      '& div:last-of-type': {
        '&:hover': {
          color: 'initial',
          backgroundColor: 'initial',
          cursor: 'pointer',
        },
      },
    }),
  })

  const readOnlyStyles: Partial<StylesConfig> = {
    control: (provided) => ({
      ...provided,
      borderColor: `${hexRgba(Primary.navy[20], 0.2)}`,
      boxShadow: 'none',
      '&:hover': {
        borderColor: `${hexRgba(Primary.navy[20], 0.2)}`,
      },
    }),
    placeholder: (provided) => ({
      ...provided,
      display: 'none',
    }),
  }

  return readOnly ? mergeStyles(defaultStyles, readOnlyStyles) : defaultStyles
}
